import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import {
    ISupplierSearchResultContact,
    StandardSupplierContactDataService,
    StandardSupplierContacts,
    SupplierSearchService,
} from '@lib/supplier';

export class ChangeSupplierContactDialogData {
    constructor(
        public readonly supplierCompanyId: string,
        public readonly companyName: string,
        public readonly currentContactId: string | null,
        public readonly companyCodeId: string | null = null,
    ) {}
}

export class ChangeSupplierContactDialogResult {
    constructor(public readonly contactId: string) {}
}

@Component({
    selector: 'demand-change-supplier-contact-dialog',
    templateUrl: './change-supplier-contact-dialog.component.html',
    standalone: false,
})
export class ChangeSupplierContactDialogComponent {
    private readonly dialogRef =
        inject<MatDialogRef<ChangeSupplierContactDialogComponent, ChangeSupplierContactDialogResult>>(MatDialogRef);
    public readonly data = inject<ChangeSupplierContactDialogData>(MAT_DIALOG_DATA);
    private readonly formBuilder = inject(FormBuilder);
    private readonly supplierSearchService = inject(SupplierSearchService);
    private readonly standardSupplierContactDataService = inject(StandardSupplierContactDataService);

    public readonly loading = signal(true);
    public readonly contacts = signal<ISupplierSearchResultContact[]>([]);
    /** Standard contact of the demand's company code, marked in the list; it does not replace the current contact. */
    public readonly standardContactId = signal<string | null>(null);

    // With exactly one contact a hint is shown instead of a dropdown; the contact is preselected
    // so it can still be assigned when no contact is set yet.
    public readonly singleContact = computed<ISupplierSearchResultContact | null>(() =>
        this.contacts().length === 1 ? this.contacts()[0] : null,
    );

    public readonly form = this.formBuilder.group({
        contactId: this.formBuilder.control<string | null>(this.data.currentContactId, [Validators.required]),
    });

    constructor() {
        this.loadContacts();
    }

    public cancel(): void {
        this.dialogRef.close();
    }

    public save(): void {
        const contactId = this.form.value.contactId;
        if (this.form.invalid || !contactId) {
            return;
        }
        this.dialogRef.close(new ChangeSupplierContactDialogResult(contactId));
    }

    private loadContacts(): void {
        // Load the contacts of the assigned supplier directly by id. The former reuse of the RFQ
        // picker search dropped the supplier whenever it fell outside the picker's RFQ/company-code
        // filtering, which showed "no contacts" despite existing ones (EPROC-11493).
        forkJoin({
            contacts: this.supplierSearchService.getContacts(this.data.supplierCompanyId),
            standardContacts: this.standardSupplierContactDataService.getSelectable(this.data.companyCodeId, [
                this.data.supplierCompanyId,
            ]),
        }).subscribe({
            next: ({ contacts, standardContacts }) => {
                this.contacts.set(contacts.filter((contact) => !!contact.person?.id));
                this.standardContactId.set(this.selectableStandardContactId(standardContacts));
                const single = this.singleContact();
                if (single) {
                    this.form.controls.contactId.setValue(single.person.id);
                }
                this.loading.set(false);
            },
            error: () => {
                this.contacts.set([]);
                this.loading.set(false);
            },
        });
    }

    private selectableStandardContactId(standardContacts: StandardSupplierContacts): string | null {
        const standardContact = standardContacts.selectableStandardContactOf(
            this.data.supplierCompanyId,
            this.contacts(),
        );
        return standardContact?.person.id ?? null;
    }
}
